import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/lib/i18n';
import { Sparkles, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * EarlyAccessCTA — reusable pre-launch waitlist banner.
 * Drop anywhere on public pages. Shows a live remaining-spots count for
 * the Personal track and links to the full /early-access page.
 */
export const EarlyAccessCTA = ({ className = '' }) => {
  const { lang } = useLanguage();
  const ja = lang === 'ja';
  const [counts, setCounts] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/early-access/counts`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setCounts)
      .catch(() => {});
  }, []);

  const total = counts?.total;
  const eaPath = ja ? '/ja/early-access' : '/en/early-access';

  return (
    <section className={`relative overflow-hidden rounded-3xl border border-purple-400/20 bg-gradient-to-br from-[#1a0f33] to-[#0f0a1f] p-8 sm:p-10 ${className}`} data-testid="early-access-cta">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(192,38,211,0.18),transparent_55%)]" />
      <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full border border-purple-400/40 bg-purple-500/10 px-3 py-1 text-xs font-semibold text-purple-200">
            <Sparkles className="w-3.5 h-3.5" /> {ja ? '先行受付受付中' : 'Early Access open'}
          </span>
          <h2 className="mt-3 text-2xl sm:text-3xl font-extrabold text-white leading-tight">
            {ja ? 'ローンチ前に枠を確保 — 1年間無料トライアル' : 'Reserve your spot — 1-Year Free Trial'}
          </h2>
          <p className="mt-2 text-sm md:text-base text-purple-100/70 max-w-xl">
            {ja
              ? 'アカウント作成は不要。メールアドレスだけで先行受付に登録できます。'
              : 'No account needed — just your email to join the Early Access waitlist.'}
          </p>
          {total && !total.full && (
            <p className="mt-2 text-xs font-mono text-purple-300/80" data-testid="ea-cta-count">
              {total.remaining.toLocaleString()} / {total.limit.toLocaleString()} {ja ? '先行受付枠 空き' : 'Early Access spots left'}
            </p>
          )}
        </div>
        <Link
          to={eaPath}
          data-testid="ea-cta-button"
          className="shrink-0 inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-purple-600 to-fuchsia-600 px-7 py-4 text-base font-bold text-white shadow-lg hover:from-purple-500 hover:to-fuchsia-500 transition-all"
        >
          {ja ? '先行受付に登録' : 'Join Early Access'} <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  );
};

export default EarlyAccessCTA;
